import React from "react";
import { NavigateFunction } from "react-router-dom";
import { ApolloClient } from "@apollo/client";
import { SampleStoryProps } from "../components/product/survey/sample-story";
import { ModalAction } from "../components/product/modal/claimable-modal";
import {
  Pronouns,
  Sex,
  SubDenialReasonEnum,
  DenyingParty,
} from "../__generated__/graphql";
import { PillCheckboxWithSubtextOption } from "../components/product/survey/pill-checkbox-group-with-subtext/pill-checkbox-group-with-subtext";
import { SpacingConfiguration } from "./spacing-types";

export enum BlockType {
  PillCheckboxGroupBlock,
  PillCheckboxGroupWithSubtextBlock,
  PillRadioButtonGroupBlock,
  PillMultiSelectButtonGroupBlock,
  PillToggleButtonBlock,
  TextareaBlock,
  TextInputBlock,
  SelectBlock,
  ReactComponentBlock,
  ListLandingBlock,
  NumberInputBlock,
  LightBulbMessageBlock,
  WarningMessageBlock,
  SampleStoryBlock,
  SuggestionBlock,
  MultiSelectBlock,
  BecauseYouSelectedBlock,
  DoneWithSectionBlock,
  SaveAnswersBlock,
  TagInputBlock,
  HeightBlock,
  TimelineBlock,
  DateBlock,
  AnswerCardBlock,
  DisclaimerBlock,
  IDontKnowBlock,
}

export type Block =
  | PillCheckboxGroupBlock
  | PillCheckboxGroupWithSubtextBlock
  | PillRadioButtonGroupBlock
  | PillMultiSelectButtonGroupBlock
  | PillToggleButtonBlock
  | TextareaBlock
  | TextInputBlock
  | SelectBlock
  | ListLandingBlock
  | ReactComponentBlock
  | NumberInputBlock
  | HeightBlock
  | LightBulbMessageBlock
  | WarningMessageBlock
  | SuggestionBlock
  | MultiSelectBlock
  | SampleStoryBlock
  | BecauseYouSelectedBlock
  | DoneWithSectionBlock
  | TimelineBlock
  | SaveAnswersBlock
  | TagInputBlock
  | DateBlock
  | AnswerCardBlock
  | DisclaimerBlock
  | IDontKnowBlock;

/**
 * @param id the presence of an Id on a block will indicate that the block can take input
 * @param requiresInput When this value is not set we will treat it as not required
 */
export type BaseBlock = {
  answerId?: string;
  type: BlockType;
  requiresInput?: boolean;
  spacing?: SpacingConfiguration;
};

export interface IDontKnowBlock extends BaseBlock {
  answerId: string;
  type: BlockType.IDontKnowBlock;
  value: string;
  label: string;
}

export interface PillCheckboxGroupBlock extends BaseBlock {
  answerId: string;
  type: BlockType.PillCheckboxGroupBlock;
  className?: string;
  title?: string;
  options: string[];
  filterAnswersOnSelected?: (answers: SurveyAnswer[]) => SurveyAnswer[];
  singleSelect?: boolean;
}

export interface PillCheckboxGroupWithSubtextBlock extends BaseBlock {
  answerId: string;
  type: BlockType.PillCheckboxGroupWithSubtextBlock;
  className?: string;
  title?: string;
  options: PillCheckboxWithSubtextOption[];
  filterAnswersOnSelected?: (answers: SurveyAnswer[]) => SurveyAnswer[];
}

export type PillButtonOption = {
  value: string;
  subtitle?: string;
  icon: "check" | "x";
};

export interface PillRadioButtonGroupBlock extends BaseBlock {
  answerId: string;
  type: BlockType.PillRadioButtonGroupBlock;
  title?: string;
  options: PillButtonOption[];
}

export interface PillMultiSelectButtonGroupBlock extends BaseBlock {
  answerId: string;
  type: BlockType.PillMultiSelectButtonGroupBlock;
  title?: string;
  options: PillButtonOption[];
}

export interface PillToggleButtonBlock extends BaseBlock {
  answerId: string;
  type: BlockType.PillToggleButtonBlock;
  label: string;
  filterAnswersOnSelected?: (answers: SurveyAnswer[]) => SurveyAnswer[];
}

export interface DoneWithSectionBlock extends BaseBlock {
  type: BlockType.DoneWithSectionBlock;
  title: string;
  subHeading: string;
}

export interface TextareaBlock extends BaseBlock {
  title?: string;
  answerId: string;
  type: BlockType.TextareaBlock;
  placeholder?: string;
}

export interface TextInputBlock extends BaseBlock {
  answerId: string;
  type: BlockType.TextInputBlock;
  title?: string;
  placeholder?: string;
  maxLength?: number;
}

export interface NumberInputBlock extends BaseBlock {
  answerId: string;
  type: BlockType.NumberInputBlock;
  title?: string;
  min?: number;
  max?: number;
  suffix?: string;
  rightSectionText?: string;
  placeholder?: string;
  decimalScale?: number;
}

export interface HeightBlock extends BaseBlock {
  answerId: string;
  type: BlockType.HeightBlock;
}

export interface SelectBlock extends BaseBlock {
  answerId: string;
  type: BlockType.SelectBlock;
  title?: string;
  options: string[];
  searchable?: boolean;
  placeholder?: string;
}

export type ListLandingOption = {
  value: string;
  isComplete: boolean;
  isMissed: boolean;
  // Function that returns the SurveyNode to navigate to when this option is clicked
  getNode: (context: SurveyContext) => SurveyNode;
};

export interface ListLandingBlock extends BaseBlock {
  type: BlockType.ListLandingBlock;
  options: ListLandingOption[];
  hasAddAnotherOption: boolean;
  addAnotherOptionPage?: string;
}

export interface LightBulbMessageBlock extends BaseBlock {
  type: BlockType.LightBulbMessageBlock;
  text: string;
}

export interface WarningMessageBlock extends BaseBlock {
  type: BlockType.WarningMessageBlock;
  title: string;
  text: string;
  whyAmISeeingThis?: string;
}

export interface SampleStoryBlock extends BaseBlock {
  type: BlockType.SampleStoryBlock;
  props: SampleStoryProps;
}

export interface SuggestionBlock extends BaseBlock {
  type: BlockType.SuggestionBlock;
  suggestions: string[];
}

export interface MultiSelectBlock extends BaseBlock {
  answerId: string;
  type: BlockType.MultiSelectBlock;
  allowCustomInput?: boolean;
  label: string;
  placeholder?: string;
  options: string[];
  filterAnswersOnSelected?: (answers: SurveyAnswer[]) => SurveyAnswer[];
}

export interface TagInputBlock extends BaseBlock {
  answerId: string;
  type: BlockType.TagInputBlock;
  label: string;
  placeholder?: string;
}

export enum DateBlockGranularity {
  Day = "day",
  Month = "month",
  Year = "year",
}

export interface DateBlock extends BaseBlock {
  answerId: string;
  type: BlockType.DateBlock;
  label: string;
  placeholder?: string;
  minDate?: Date;
  maxDate?: Date;
  granularity?: DateBlockGranularity;
  monthFormat?: string;
}

export interface AnswerCardBlock extends BaseBlock {
  answerId: string;
  type: BlockType.AnswerCardBlock;
  title: string;
  options: AnswerCardBlockOption[];
}

export type AnswerCardBlockOption = {
  value: string;
  description: string;
};

export interface BecauseYouSelectedBlock extends BaseBlock {
  type: BlockType.BecauseYouSelectedBlock;
  options: string[];
}

export interface ReactComponentBlock extends BaseBlock {
  type: BlockType.ReactComponentBlock;
  component: React.JSX.Element | string;
}

export interface TimelineBlock extends BaseBlock {
  type: BlockType.TimelineBlock;
  answerId: string;
  startDate: Date;
  currentValue?: number | string | null;
  startingValue: number | string | null;
  timelineType: "dosage" | "weight";
  unitLabel?: string;
  options?: string[];
}

export interface SaveAnswersBlock extends BaseBlock {
  type: BlockType.SaveAnswersBlock;
  keepAnswers: (answers: SurveyAnswer[]) => SurveyAnswer[];
}

export interface DisclaimerBlock extends BaseBlock {
  type: BlockType.DisclaimerBlock;
  title: string;
  disclaimer: string;
}

export type ExitFlowProps = {
  ctx: SurveyContext;
  close: () => void;
  continueForward: () => void;
  navigate: NavigateFunction;
  saveAnswers: (options?: {
    includeSurveyCompleted?: boolean;
  }) => Promise<void>;
  apolloClient: ApolloClient;
};

export type TempModalProps = {
  title: string;
  content: React.ReactNode;
  primaryAction: ModalAction;
  secondaryAction?: ModalAction;
};

export interface SurveyNode {
  id: string;
  optionalTag?: boolean;
  title?: string;
  blocks: Block[];
  // context passed to these functions will always have the answer from this node already folded in
  next?: (ctx: SurveyContext) => SurveyNode[];
  // refresh is called after an answer is changed, if defined. This can be used to make intra node block changes based on answers.
  refresh?: (ctx: SurveyContext) => SurveyNode;
  // used to go back to a ListLanding component after navigating through all the nodes in a branch
  goToLanding?: (ctx: SurveyContext) => SurveyNode;
  // Check all conditions are met to continue on from this node
  canContinue?: boolean;
  // A way for us to force a user out of the app if some condition is met.
  exitFlow?: (
    exitFlowProps: ExitFlowProps,
  ) => Promise<TempModalProps | undefined>;
}

export interface SurveyContext {
  appealId: string;
  condition: string;
  denialReason: string;
  subDenialReasons: SubDenialReasonEnum[];
  medication: string;
  answers: SurveyAnswer[];
  patient: {
    firstName: string;
    lastName: string;
    pronouns: Pronouns | null;
    sex: Sex | null;
  };
  denyingParty: DenyingParty | null;
  otherDenyingPartyName: string | null;
  pbmName: string | null;
  providerName: string | null;
  isExpeditedReview: boolean | null;
}

export type AnswerValue = string | string[];

export interface SurveyAnswer {
  id: string;
  value: AnswerValue;
}

export interface TimelineEntry {
  date: string;
  value: string | null;
}
